import React from 'react';
import * as TYPES from '@contentful/rich-text-types';
import styled from 'styled-components';
import { GatsbyImage } from 'gatsby-plugin-image';

import ArrowLink from '@elements/ArrowLink';
import SingleQuote from '@sections/SingleQuote';
import Link from '@util/link';
import Video from '@basic/Video';

import AnimatedStatBlockLink from '../../custom/home/AnimatedStatBlockLink';

// For Google Sheet Table embedded for /processors
import ProcessorTable from '@basic/GoogleSheetTable';

import { ImCheckmark } from 'react-icons/im';

import {
	BodyText20,
	BodyText18,
	BodyText16,
	BodyText14,
	SmallSubheadingH2,
	SubheadingH2,
	SmallSubheadingH3,
	SubheadingH3,
	SmallSubheadingH4,
	SubheadingH4,
	BaseSmallHeadingSm,
} from '../Typography';

const LiStyles = styled.ul`
	margin-left: 2rem; /* space for the numbers */
	padding-left: 0; /* avoid double-indent */
	list-style: disc;

	li::marker {
		font-size: 1.4rem;
		color: ${({ theme }) => theme.colors.black.main};
	}
`;

const OL = styled.ol`
	list-style: decimal outside; /* ensure numbers show properly */
	margin-left: 2rem; /* space for the numbers */
	padding-left: 0; /* avoid double-indent */
	li::marker {
		font-size: 1.4rem;
		color: ${({ theme }) => theme.colors.black.main};
	}
`;

const StyledLink = styled(Link)`
	color: ${({ theme }) => theme.colors.black.main};
	text-decoration: underline;
	text-underline-offset: 2px;
	font-family: ${props => props.theme.fonts.body};

	&:hover {
		color: ${({ theme }) => theme.colors.primary.main};
		text-decoration: underline;
	}
`;

const ImageWrapper = styled.div`
	margin: 4rem 0;
	img {
		width: 100%;
	}
`;

const WrappedImage = styled.div`
	width: ${({ imageWrap }) =>
		imageWrap === 'Left' || imageWrap === 'Right' ? '50%' : '100%'};
	float: ${({ imageWrap }) =>
		imageWrap === 'Left' ? 'left' : imageWrap === 'Right' ? 'right' : 'none'};
	height: auto;
	margin: 2rem;

	img {
		width: 100%;
		object-fit: contain;
	}
`;

const Caption = styled(BodyText18)`
	background-color: ${({ theme }) => theme.colors.black.main};
	color: ${({ theme }) => theme.colors.white.main};
	text-align: center;
	padding: 14px;
`;

const BlockquoteStyle = styled(BaseSmallHeadingSm)`
	background-color: ${({ theme }) => theme.colors.cream.main};
	padding: 2rem;
	border-radius: ${({ theme }) => theme.borderRadius.standard}px;
	margin: 2rem 0;
	text-align: center;
	text-wrap: pretty;

	p {
		margin-bottom: 0;
	}
`;

const BasicTable = styled.table`
	table-layout: auto;
	width: 100%;
	margin: 2rem 0;
	tr {
		th {
			background-color: ${({ theme }) => theme.colors.navy.main};
			padding: 2rem;

			p {
				margin-bottom: 0;
				color: ${({ theme }) => theme.colors.white.main};
			}
		}

		td {
			border-bottom: 1px solid ${({ theme }) => theme.colors.black.main};

			&:not(:last-child) {
				border-right: 1px solid ${({ theme }) => theme.colors.black.main};
			}

			p {
				padding: 2rem;
			}
		}
	}
`;

const standardRichTextStyling = (options = {}) => ({
	renderNode: {
		[TYPES.BLOCKS.PARAGRAPH]: (node, children) => {
			if (options?.bodySize === 20)
				return <BodyText20 as="p">{children}</BodyText20>;
			if (options?.bodySize === 18)
				return <BodyText18 as="p">{children}</BodyText18>;
			if (options?.bodySize === 16)
				return <BodyText16 as="p">{children}</BodyText16>;
			if (options?.bodySize === 14)
				return <BodyText14 as="p">{children}</BodyText14>;
			if (options?.header === 'smallHeader')
				return <SmallSubheadingH2 as="p">{children}</SmallSubheadingH2>;

			// Default fallback
			return <p>{children}</p>;
		},

		[TYPES.BLOCKS.HEADING_2]: (node, children) =>
			options.smallHeadings ? (
				<SmallSubheadingH2>{children}</SmallSubheadingH2>
			) : (
				<SubheadingH2>{children}</SubheadingH2>
			),

		[TYPES.BLOCKS.HEADING_3]: (node, children) =>
			options.smallHeadings ? (
				<SmallSubheadingH3>{children}</SmallSubheadingH3>
			) : (
				<SubheadingH3>{children}</SubheadingH3>
			),

		[TYPES.BLOCKS.HEADING_4]: (node, children) =>
			options.smallHeadings ? (
				<SmallSubheadingH4>{children}</SmallSubheadingH4>
			) : (
				<SubheadingH4>{children}</SubheadingH4>
			),

		[TYPES.BLOCKS.QUOTE]: (node, children) => {
			return <BlockquoteStyle as="blockquote">{children}</BlockquoteStyle>;
		},
		[TYPES.BLOCKS.UL_LIST]: (node, children) => <LiStyles>{children}</LiStyles>,
		[TYPES.BLOCKS.OL_LIST]: (node, children) => <OL>{children}</OL>,
		[TYPES.INLINES.HYPERLINK]: node => {
			const linkText = node.content.map(c => c.value).join(' ');

			if (node.data.uri.indexOf('google_sheet') >= 0) {
				return (
					<ProcessorTable
						id={node.data.uri.slice(
							node.data.uri.indexOf('=') + 1,
							node.data.uri.length
						)}
					/>
				);
			}

			if (options?.linkType === 'arrow') {
				return (
					<ArrowLink link={node.data.uri} linkText={linkText} arrowSize={20} />
				);
			}

			return <StyledLink to={node.data.uri}>{linkText}</StyledLink>;
		},

		[TYPES.INLINES.EMBEDDED_ENTRY]: node => {
			const content = node.data.target;

			if (options.skipEmbed) return null; // skip rendering inline embeds
		},

		[TYPES.INLINES.ASSET_HYPERLINK]: node => {
			const linkText = node.content[0].value;
			const link = node.data.target.file.url;

			return <StyledLink to={link}>{linkText}</StyledLink>;
		},

		[TYPES.BLOCKS.EMBEDDED_ASSET]: node => {
			const image = node.data.target;

			return (
				image && (
					<ImageWrapper className="rich-text_img">
						{image?.gatsbyImageData ? (
							<GatsbyImage image={image.gatsbyImageData} alt={image?.title} />
						) : (
							<img src={image?.file.url} alt={image?.title} />
						)}
						{options?.noCaptions || !image?.title ? null : (
							<Caption>{image?.title}</Caption>
						)}
					</ImageWrapper>
				)
			);
		},
		[TYPES.BLOCKS.TABLE]: (node, children) => {
			return <BasicTable>{children}</BasicTable>;
		},
		[TYPES.BLOCKS.TABLE_CELL]: (node, children) => {
			// children here is an array of rendered child nodes (usually text spans)
			return (
				<td>
					{node.content.map((child, index) => {
						return child.content.map(childEl => {
							console.log(childEl);
							if (childEl.value === '✔️') return <ImCheckmark />;
							return <BodyText18>{childEl.value}</BodyText18>;
						});
					})}
				</td>
			);
		},
		[TYPES.BLOCKS.EMBEDDED_ENTRY]: node => {
			const content = node.data.target;
			if (options.skipEmbed) return null;

			if (content?.type === 'ContentfulModuleQuote') {
				return (
					<SingleQuote
						smallQuote={options.smallHeadings}
						noTopPadding
						noBottomPadding
						content={content}
					/>
				);
			}
			if (content?.type === 'ContentfulElementBasicVideo')
				return (
					<Video
						url={content.videoLink}
						thumbnail={
							content.videoThumbnail ? content.videoThumbnail.file.url : null
						}
					/>
				);

			if (content?.type === 'ContentfulModuleFeatureBlock') {
				return (
					<WrappedImage imageWrap={content.imageWrap2}>
						<img src={content?.image.file.url} alt={content?.imageCaption} />
						{!content?.imageCaption ? null : (
							<Caption color="white" component="b4">
								{content?.imageCaption}
							</Caption>
						)}
					</WrappedImage>
				);
			}

			if (content?.type === 'ContentfulElementBasicImage') {
				return (
					<WrappedImage imageWrap={content.ImageWrap[0]}>
						<img src={content?.image.file.url} alt={content?.imageCaption} />
						{!content?.imageCaption ? null : (
							<Caption color="white" component="b4">
								{content?.imageCaption}
							</Caption>
						)}
					</WrappedImage>
				);
			}

			if (content?.type === 'ContentfulQuoteWithBackgroundColor') {
				return (
					<SingleQuote
						smallQuote
						content={{
							quote: { quote: content.quoteBody.quoteBody },
							quoteAuthor: { authorName: content.quoteAuthor },
						}}
					/>
				);
			}

			if (content?.type === 'ContentfulStatBlock' && options.animatedStatLink) {
				return <AnimatedStatBlockLink stat={content} />;
			}

			return null; // default
		},
	},
});

export default standardRichTextStyling;
